import Vue from "vue"
import Vuex from "vuex"
import Helpers from "@/helpers.js"
import cfg from "@/cfg"
import { accounts, currentUser, ui, homepage, users, teams, cbroute, buckets, cases, timeline, caseTypes, metrics, widgets, folders, chat, kanban } from "@/stores/manifest.js"

Vue.use(Vuex)

let refreshSessionRequest = null

const refreshAccessTokenUsingRefreshToken = async () => {
  const refreshToken = window.localStorage.getItem("refreshSession")
  if (!refreshToken) {
    console.warn("[AuthDebug][Store.authenticate] no refresh token available")
    return false
  }

  console.warn("[AuthDebug][Store.authenticate] access token invalid, attempting refresh")

  try {
    const response = await fetch(cfg.graphqlEndPoint(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        query: `
          mutation RefreshSession($refreshToken: String!) {
            refreshSession(refreshToken: $refreshToken) {
              jwToken
              accessToken
              refreshToken
              error
              message
            }
          }
        `,
        variables: { refreshToken },
      }),
    })

    if (!response.ok) {
      console.warn("[AuthDebug][Store.authenticate] refresh request failed", { status: response.status })
      return false
    }

    const parsed = await response.json()
    const payload = parsed && parsed.data && parsed.data.refreshSession
    const nextAccessToken = payload && (payload.accessToken || payload.jwToken)
    if (!payload || payload.error || !nextAccessToken) {
      console.warn("[AuthDebug][Store.authenticate] refresh payload invalid", {
        hasPayload: !!payload,
        error: payload && payload.error,
        message: payload && payload.message,
      })
      return false
    }

    window.localStorage.setItem("session", nextAccessToken)
    if (payload.refreshToken) {
      window.localStorage.setItem("refreshSession", payload.refreshToken)
    }
    console.info("[AuthDebug][Store.authenticate] refresh succeeded; session updated")
    return true
  } catch (error) {
    console.warn("[AuthDebug][Store.authenticate] refresh threw", { error: error && error.message ? error.message : String(error) })
    return false
  }
}

const createStore = (provider) => {
  return new Vuex.Store({
    state: {
      provider: provider,
      session: {},
      invalidLogin: false,
      invalidLoginMessage: "",
    },
    mutations: {
      updateSession(state, payload) {
        const token = payload && typeof payload === "object" ? payload.token : payload
        const refreshToken = payload && typeof payload === "object" ? payload.refreshToken : undefined
        if (token) {
          window.localStorage.setItem("session", token)
          if (refreshToken) {
            window.localStorage.setItem("refreshSession", refreshToken)
          }
          this.state.session = token
        } else {
          window.localStorage.removeItem("session")
          window.localStorage.removeItem("refreshSession")
          this.state.session = undefined
        }
      },
      updateInvalidLogin(state, { isInvalid, message }) {
        this.state.invalidLogin = isInvalid
        this.state.invalidLoginMessage = message
      },
      clearInvalidLogin() {
        this.state.invalidLogin = false
        this.state.invalidLoginMessage = ""
      },
    },
    actions: {
      async sortArray({ commit }, arrayData) {
        const arrayToSort = arrayData.array.map(function (x) {
          return x
        })
        arrayToSort.sort(function (a, b) {
          const aVal = a[arrayData.prop]
          const bVal = b[arrayData.prop]
          if (aVal > bVal) {
            return -1
          }
          if (aVal < bVal) {
            return 1
          }
          return 0
        })
        if (arrayData.type === "UserCard") {
          commit("setUsers", arrayToSort)
        }
        if (arrayData.type === "TeamCard") {
          commit("setTeams", arrayToSort)
        }
      },
      async searchArray({ commit, dispatch }, arrayData) {
        let outputArray
        if (arrayData.term !== "") {
          outputArray = arrayData.array.filter((item) => {
            let found = false
            arrayData.searchableBy.forEach((searchField) => {
              if (item[searchField].toString().toLowerCase().includes(arrayData.term.toLowerCase())) {
                found = true
              }
            })
            return found
          })
        } else {
          if (arrayData.type === "UserCard") {
            commit("setUsers", this.dispatch("getUsers"))
          }
          if (arrayData.type === "TeamCard") {
            dispatch("requestCurrentUser")
          }
        }
        if (arrayData.type === "UserCard") {
          commit("setUsers", outputArray)
        }
        if (arrayData.type === "TeamCard") {
          commit("setTeams", outputArray)
        }
      },
      logout({ commit }) {
        commit("clearFlashMessage")
        commit("updateSession", undefined)
      },
      async requestForgotPassword({ dispatch }, email) {
        try {
          await provider.requestForgotPassword(email)
        } catch (err) {
          dispatch("error", "Unable to reset password")
        }
      },
      async resetPassword({ commit }, { password, token }) {
        try {
          const response = await provider.resetPassword(password, token)
          if (response.error) {
            commit("displayFlashMessage", { message: response.lowLevelErrorMessage, severity: "error" })
          } else {
            commit("displayFlashMessage", { message: "Password set. Please log in.", severity: "success" })
            Vue.router.push({ name: "Login" })
          }
        } catch (err) {
          commit("displayFlashMessage", { message: err.message, severity: "error" })
        }
      },
      async clearInvalidLogin({ commit }) {
        commit("clearInvalidLogin")
      },
      async authenticate() {
        const token = window.localStorage.getItem("session")
        if (Helpers.isJwtValid(token)) {
          return true
        }

        if (!refreshSessionRequest) {
          refreshSessionRequest = refreshAccessTokenUsingRefreshToken().finally(() => {
            refreshSessionRequest = null
          })
        }

        const refreshed = await refreshSessionRequest
        if (!refreshed) {
          window.localStorage.removeItem("session")
          window.localStorage.removeItem("refreshSession")
          console.warn("[AuthDebug][Store.authenticate] refresh failed; clearing session and returning unauthenticated")
          return false
        }

        return true
      },
    },
    getters: {
      getlastViewed: (state) => {
        return state.homepage.recent_cases
      },
      getApiEndPoint: () => {
        return Helpers.removingTrailingSlash(cfg.apiEndPoint())
      },
    },
    modules: {
      accounts,
      currentUser,
      ui,
      homepage,
      users,
      teams,
      cbroute,
      buckets,
      cases,
      timeline,
      caseTypes,
      metrics,
      widgets,
      folders,
      chat,
      kanban,
    },
  })
}

export default createStore
